import { Component, Fragment } from 'react'
import { Helmet } from 'react-helmet'

import emitter from 'garden/src/js/utils/emitter'
import debounce from 'garden/src/js/utils/debounce'

import * as wishlistsRepository from 'scripts/api/wishlists'
import profile from 'scripts/components/user/profile'

import { Breadcrumb } from 'reactSite/Breadcrumb'

import Spinner from 'reactUtils/Spinner'

import { wishlistSchema } from '../schema'
import Title from '../components/Title'
import HomeScreen from '../components/HomeScreen'
import ModifyWishlist from '../components/drawer/lists/ModifyWishlist'
import WishlistCollection from '../components/lists/WishlistCollection'
import { sendUrlInPostMessage } from '../../../utils/postMessageRN'

class MyWishlists extends Component {
  constructor (props) {
    super(props)

    const breadcrumbNodes = [
      {
        id: '1',
        name: 'Home',
        url: '/'
      },
      {
        id: '2',
        name: 'Minhas Listas de Favoritos',
        url: 'minhas-listas-de-desejos'
      }
    ]

    this.state = {
      breadcrumbNodes: breadcrumbNodes,
      wishlists: [],
      isOpenedDrawer: false,
      isEditList: false,
      isLogged: false,
      hasError: false,
      slug: '',
      spinnerVisible: false
    }
  }

  componentDidMount () {
    this.changeUserState()
    sendUrlInPostMessage()
  }

  componentWillUnmount () {
    emitter.removeAllListeners()
  }

  loadList = async () => {
    this.toggleSpinner()

    try {
      const lists = await wishlistsRepository.lists()

      if (lists.status !== 200) {
        throw new Error(`Cannot get wishlists, API returns status: ${lists.status}`)
      }

      this.setState({
        wishlists: lists.body.data,
        spinnerVisible: false
      })
    } catch (err) {
      this.setState({
        hasError: true,
        spinnerVisible: false
      })
    }
  }

  modifyList = debounce(async (listData) => {
    this.toggleSpinner()

    const formatData = {
      name: listData.get('wishlist-name'),
      background_image: listData.get('thumb-wishlist')
    }

    try {
      const response = await (this.state.isEditList
        ? wishlistsRepository.editList(formatData, this.state.slug)
        : wishlistsRepository.createList(formatData))

      if (response.status !== 201 && response.status !== 200) {
        this.emitErrorsMessage(response.body.errors)
        this.toggleSpinner()
      } else {
        this.emitSuccessMessage(`Lista de favoritos ${response.status === 201 ? 'criada' : 'atualizada'}!`)
        this.closeDrawer()
        this.loadList()
      }
    } catch (err) {
      this.toggleSpinner()
      this.emitErrorsMessage('Não conseguimos adicionar essa lista de favoritos, tente novamente.')
    }
  }, 500)

  removeList = debounce(async (item) => {
    this.toggleSpinner()

    try {
      const response = await wishlistsRepository.removeList(item)

      if (response.status !== 204) {
        this.emitErrorsMessage(response.body.errors)
        this.toggleSpinner()
      } else {
        this.emitSuccessMessage('Lista de favoritos removida!')
        this.loadList()
      }
    } catch (err) {
      this.toggleSpinner()
      this.emitErrorsMessage('Não conseguimos remover essa lista de favoritos, tente novamente.')
    }
  }, 500)

  openDrawer = (editOptions = {}) => {
    const { isEdit, slug } = editOptions
    this.setState({
      isOpenedDrawer: true,
      isEditList: isEdit,
      slug: slug
    })
  }

  closeDrawer = () => {
    this.setState({
      isOpenedDrawer: false,
      isNewList: false
    })
  }

  emitSuccessMessage (success) {
    emitter.emit('stickyFeedback:success', {
      title: 'Sucesso:',
      content: success
    })
  }

  emitErrorsMessage (error) {
    emitter.emit('stickyFeedback:error', {
      title: 'Ops! Ocorreu o seguinte erro:',
      content: error
    })
  }

  toggleSpinner = () => {
    this.setState(prevState => ({
      spinnerVisible: !prevState.spinnerVisible
    }))
  }

  changeUserState = async () => {
    this.toggleSpinner()
    const isLoggedIn = await profile.isLoggedIn()
    this.setState({
      isLogged: isLoggedIn
    })
    isLoggedIn && this.loadList()
    this.toggleSpinner()
  }

  redirectToLogin = () => {
    const currentUrl = encodeURIComponent(window.location.href)
    window.location = `/login?redirect=${currentUrl}`
  }

  renderLists = (userLists) => (
    !this.state.hasError
      ? (
        <WishlistCollection
          wishlists={userLists}
          onCreate={this.openDrawer}
          onEdit={this.openDrawer}
          onRemove={this.removeList}
        />
        )
      : (
        <section className="offers-fetch-error">
          <h1>
            Ocorreu um erro ao carregar suas listas,
            tente novamente em instantes.
          </h1>
        </section>
        )
  )

  render () {
    const {
      breadcrumbNodes,
      wishlists,
      isOpenedDrawer,
      isLogged,
      isEditList
    } = this.state

    const FoldersProps = {
      isOpenedDrawer: isOpenedDrawer,
      closeDrawer: this.closeDrawer,
      modifyList: this.modifyList,
      isEditList: isEditList
    }

    return (
      <Fragment>
        <Helmet>
          <title>Minhas Listas de Favoritos - Leroy Merlin</title>
          <meta
            name="description"
            content="Meus projetos na Leroy Merlin. Crie um projeto novo ou
              acesse para conferir todos os produtos e fotos escolhidas aqui."
          />
          <meta property="og:description"
            content="Meus projetos na Leroy Merlin. Crie um projeto novo ou
              acesse para conferir todos os produtos e fotos escolhidas aqui."
          />
          <script type="application/ld+json">
            {`${JSON.stringify(wishlistSchema())}`}
          </script>
        </Helmet>
        <Spinner visible={this.state.spinnerVisible} />
        <ModifyWishlist {...FoldersProps} />
        <div className="wishlist-page-header">
          <div className="container">
            { breadcrumbNodes &&
              <Breadcrumb
                nodes={breadcrumbNodes}
              />
            }
            { isLogged && <Title name={'Minhas Listas de Favoritos'}/> }
          </div>
        </div>
        {
          isLogged
            ? this.renderLists(wishlists)
            : <HomeScreen trigger={this.redirectToLogin} />
        }
      </Fragment>
    )
  }
}

export default MyWishlists
